import type {ReactNode} from 'react';
import {AlignLeft, Eye, Glasses, Image, Layers, Scan, Sun, UserRound} from 'lucide-react';
import {VelaScene, type VelaContext, type VelaRule} from '../engine/types';

type TipSet = 'doc_camera' | 'doc_gallery' | 'selfie_camera' | 'selfie_gallery';

type Tip = {id: string; icon: ReactNode};

const SIDE_TIP_ID = 'side';

const TIPS: Record<TipSet, Tip[]> = {
  doc_camera: [
    {id: 'flat', icon: <Layers />},
    {id: 'frame', icon: <Scan />},
    {id: 'glare', icon: <Sun />},
    {id: SIDE_TIP_ID, icon: null},
  ],
  doc_gallery: [
    {id: 'original', icon: <Image />},
    {id: 'corners', icon: <Scan />},
    {id: 'sharp', icon: <Sun />},
    {id: SIDE_TIP_ID, icon: null},
  ],
  selfie_camera: [
    {id: 'oval', icon: <UserRound />},
    {id: 'light', icon: <Sun />},
    {id: 'look', icon: <Eye />},
    {id: 'cover', icon: <Glasses />},
  ],
  selfie_gallery: [
    {id: 'only_you', icon: <UserRound />},
    {id: 'straight', icon: <Eye />},
    {id: 'lit', icon: <Sun />},
    {id: 'recent', icon: <Image />},
  ],
};

const DOCUMENT_SCENES = new Set([VelaScene.IVFront, VelaScene.IVBack]);

export function getPhotoTipSet(ctx: VelaContext): TipSet | null {
  if (ctx.captureMode === 'intro') return null;
  const mode = ctx.captureMode === 'camera' ? 'camera' : 'gallery';
  if (ctx.scene === VelaScene.IVSelfie) return `selfie_${mode}`;
  if (DOCUMENT_SCENES.has(ctx.scene)) return `doc_${mode}`;
  return null;
}

const TIP_COUNT = 4;

export const photoTipRules: VelaRule[] = Array.from({length: TIP_COUNT}, (_, index) => ({
  id: `iv/photo-tip-${index + 1}`,
  section: 'photoTips',
  scenes: [VelaScene.IVFront, VelaScene.IVBack, VelaScene.IVSelfie],
  priority: TIP_COUNT - index,
  evaluate: (ctx, t) => {
    const set = getPhotoTipSet(ctx);
    if (!set) return null;

    const tip = TIPS[set][index];
    if (tip.id !== SIDE_TIP_ID) {
      return {
        id: `${set}-${tip.id}`,
        icon: tip.icon,
        title: t(`tip.${set}.${tip.id}.title`),
        description: t(`tip.${set}.${tip.id}.body`),
      };
    }

    const isBack = ctx.scene === VelaScene.IVBack;
    return {
      id: `${set}-${isBack ? 'side-back' : 'side-front'}`,
      icon: isBack ? <AlignLeft /> : <UserRound />,
      title: t(`tip.${set}.side_${isBack ? 'back' : 'front'}.title`),
      description: t(`tip.${set}.side.body`),
    };
  },
}));
